import * as React from "react";
import {
  parseVimeoId,
  parseYoutubeId,
  YoutubePlayerData,
} from "@plinknz/tah-website-elements";
import { useScript } from "../../utility/hooks/use-script";

export interface YoutubePlayerProps {
  data: YoutubePlayerData;
}

interface VideoProps {
  id: string;
  title: string;
  description: string;
}

const DEFAULT_RENDER_TIMEOUT = 125;

const YoutubePlayer = ({ id, title, description }: VideoProps) => {
  const playerRef = React.useRef<HTMLDivElement>();

  const renderPlayer = (timeout = DEFAULT_RENDER_TIMEOUT) => {
    if (typeof window?.YT?.Player !== "undefined") {
      // eslint-disable-next-line no-new
      new window.YT.Player(playerRef.current, {
        videoId: id,
        playerVars: {
          origin: window.location.origin,
          modestbranding: 1,
          rel: 0,
        },
      });
    } else {
      // HACK: to get multple videos on a page working
      setTimeout(() => renderPlayer(timeout * 2), timeout);
    }
  };

  useScript(playerRef, "https://www.youtube.com/iframe_api");

  React.useEffect(() => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-explicit-any
    const thisWindow = window as any;

    if (!thisWindow.onYouTubeIframeAPIReady) {
      thisWindow.onYouTubeIframeAPIReady = renderPlayer;
    } else {
      renderPlayer();
    }
  }, []);

  return (
    <section className="video || constrain-width" data-testid="youtube-player">
      {title && <h3 className="heading-3 || video-header">{title}</h3>}
      {description && <p className="video-text">{description}</p>}
      <div className="video-inner">
        <div className="video-player" ref={playerRef} />
      </div>
    </section>
  );
};

const VimeoPlayer = ({ id, title, description }: VideoProps) => {
  const playerRef = React.useRef<HTMLDivElement>();

  const renderPlayer = (timeout = DEFAULT_RENDER_TIMEOUT) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const thisWindow = window as any;
    if (typeof thisWindow?.Vimeo?.Player !== "undefined") {
      // eslint-disable-next-line no-new
      new thisWindow.Vimeo.Player(playerRef.current, { id });
    } else {
      // HACK: to get multple videos on a page working
      setTimeout(() => renderPlayer(timeout * 2), timeout);
    }
  };

  useScript(playerRef, "https://player.vimeo.com/api/player.js");

  React.useEffect(() => {
    renderPlayer();
  }, []);

  return (
    <section className="video || constrain-width" data-testid="youtube-player">
      {title && <h3 className="heading-3 || video-header">{title}</h3>}
      {description && <p className="video-text">{description}</p>}
      <div className="video-inner">
        <div className="video-player" ref={playerRef} />
      </div>
    </section>
  );
};

export const YoutubePlayerContentBlock = ({ data }: YoutubePlayerProps) => {
  const ytId = parseYoutubeId(data.youtube_url);
  if (ytId) {
    return YoutubePlayer({
      id: ytId,
      title: data.title,
      description: data.description,
    });
  }

  const vimeoId = parseVimeoId(data.youtube_url);

  if (!vimeoId) {
    return null;
  }

  return VimeoPlayer({
    id: data.youtube_url,
    title: data.title,
    description: data.description,
  });
};
